// orgMembersPaging — pure helpers for GET /orgs/:orgId/members' pagination +
// background-import envelope (server-is-the-org import, T4). The endpoint is
// migrating from a bare array to `{ members, nextCursor?, importStatus? }`;
// both shapes may be live simultaneously across deploys, so every caller
// runs the raw response through normalizeOrgMembersResponse rather than
// assuming a shape. Kept side-effect-free so OrgMembersPanel's pagination/
// polling logic is unit-testable without mounting React.
//
// Run: npx vitest run src/lib/orgMembersPaging.test.ts

import type { OrgMemberImportStatus, OrgMembersResponse, ScopedOrgMember } from './api';

// normalizeOrgMembersResponse — array = legacy (pre-pagination): treat as
// members with no nextCursor/importStatus. Envelope = new shape, passed
// through as-is (missing fields default to empty/undefined, never throw).
export function normalizeOrgMembersResponse(
  raw: ScopedOrgMember[] | OrgMembersResponse | null | undefined
): OrgMembersResponse {
  if (Array.isArray(raw)) return { members: raw };
  if (!raw) return { members: [] };
  return {
    members: raw.members ?? [],
    nextCursor: raw.nextCursor,
    importStatus: raw.importStatus,
  };
}

// mergeMemberPages — accumulate a newly-fetched page onto an already-loaded
// list. Deduped by discordId so a refetched/overlapping page (e.g. page one
// re-polling while later pages are loaded) never double-lists a row; on a
// collision the incoming copy's data wins (freshest role/name), but the
// row's POSITION stays wherever it first appeared — Load-more never
// reshuffles rows the admin has already seen.
export function mergeMemberPages(
  existing: ScopedOrgMember[],
  incoming: ScopedOrgMember[]
): ScopedOrgMember[] {
  const byId = new Map<string, ScopedOrgMember>();
  for (const m of existing) byId.set(m.discordId, m);
  for (const m of incoming) byId.set(m.discordId, m);
  return Array.from(byId.values());
}

// isImportInFlight — QUEUED/RUNNING is the only window the banner shows and
// the poll stays live; DONE/FAILED/undefined all stop it (DONE shows nothing
// special — the next ordinary refetch just carries the fresh list).
export function isImportInFlight(status: OrgMemberImportStatus | undefined): boolean {
  return status?.state === 'QUEUED' || status?.state === 'RUNNING';
}
